import { type FormikHelpers, useFormik } from "formik";
import { useState } from "react";
import * as yup from "yup";

import { useSubmitEmailMutation } from "~/feature/auth/authApi";
import { createEmailValidationSchema } from "~/feature/auth/emailValidationSchema";
import { useRecaptchaToken } from "~/feature/auth/hooks/useRecaptchaToken";
import { recaptchaErrorText } from "~/feature/auth/recaptchaToken";
import type { GetUpdatesSource } from "~/feature/auth/type";
import { markSubscribed } from "~/feature/getUpdates/utils/getUpdatesStorage";
import { handleError } from "~/utils/handleError";

const EMAIL_ERROR =
  "That address looks incomplete. Check the domain and try again.";

interface IGetUpdatesFormValues {
  email: string;
  recaptchaToken: string;
}

const initialValues: IGetUpdatesFormValues = {
  email: "",
  recaptchaToken: "",
};

const schema = yup.object({
  email: createEmailValidationSchema({
    invalidDomain: EMAIL_ERROR,
    invalidEmail: EMAIL_ERROR,
    required: EMAIL_ERROR,
  }),
});

export const useGetUpdatesForm = (source: GetUpdatesSource) => {
  const requestRecaptchaToken = useRecaptchaToken();
  const [submitEmail] = useSubmitEmailMutation();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const submitForm = async (
    values: IGetUpdatesFormValues,
    helpers: FormikHelpers<IGetUpdatesFormValues>
  ) => {
    try {
      const recaptcha = await requestRecaptchaToken("signup");

      if (!recaptcha.ok) {
        helpers.setFieldError(
          "recaptchaToken",
          recaptchaErrorText(recaptcha.reason)
        );
        return;
      }

      helpers.setFieldError("recaptchaToken", "");

      const email = values.email.trim();
      await submitEmail({
        email,
        recaptchaToken: recaptcha.token,
        source,
      }).unwrap();
      markSubscribed();
      setSubmittedEmail(email);
    } catch (error) {
      handleError(error);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const formik = useFormik<IGetUpdatesFormValues>({
    initialValues,
    validationSchema: schema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: submitForm,
  });

  const showRecaptchaError =
    formik.submitCount > 0 && Boolean(formik.errors.recaptchaToken);

  return { formik, submittedEmail, showRecaptchaError };
};
